/*
Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.

Licensed under the Apache License, Version 2.0 (the "License").
You may not use this file except in compliance with the License.
You may obtain a copy of the License at

    http://www.apache.org/licenses/LICENSE-2.0

Unless required by applicable law or agreed to in writing, software
distributed under the License is distributed on an "AS IS" BASIS,
WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
See the License for the specific language governing permissions and
limitations under the License.
*/
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import { Grid } from '@mui/system';
import {
  ModelConfigDtoFunctionModuleEnum,
  ModelConfigDtoModelEnum,
} from '@ink-ai/insight-service-sdk';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '@ink-ai/portal/reducers';
import { configuration } from '@ink-ai/portal/reducers/configuration';

// Only models the MGA gateway serves WITHOUT re-routing (verified: request name
// == returned model). Older names like gemini-2.5-pro / gpt-4o / DeepSeek-R1-mga
// get silently re-routed by the gateway (to gemini-3.1-pro / gpt-5-mini / glm,
// etc.), so "what the user picks" wouldn't match "what actually runs" — those
// are intentionally not listed here.
export const models: Record<string, string> = {
  'CLAUDE_SONNET_5': 'Claude Sonnet 5',
  'CLAUDE_OPUS_5': 'Claude Opus 5',
  'GPT_5_4': 'GPT-5.4',
  'GEMINI_3_1_PRO': 'Gemini 3.1 Pro',
  // Kimi K3 hidden from the selectable list (kept in the enum/backend so
  // existing configs still resolve); re-add here to expose it again.
  // 'KIMI_K3': 'Kimi K3',
};

interface ModelConfigForFunctionModuleProps {
  functionModule: ModelConfigDtoFunctionModuleEnum;
}

export const ModelConfigForFunctionModule = ({
  functionModule,
}: ModelConfigForFunctionModuleProps) => {
  const { t } = useTranslation();
  const modelConfig = useSelector(
    (state: RootState) => state.configuration.modelConfig ?? [],
  );
  const dispatch = useDispatch();
  const model =
    modelConfig.find(
      (modelConfig) => modelConfig.functionModule === functionModule,
    // Keep in sync with DEFAULT_TRANSLATION_MODEL in
    // api-service/src/user/configuration/configuration.service.ts, otherwise
    // Settings shows a different model than the one actually used.
    )?.model ?? ('GEMINI_3_1_PRO' as any);
  const { name, description } = {
    name: t(`modelConfig.functionModules.${functionModule}.name`),
    description: t(`modelConfig.functionModules.${functionModule}.description`),
  };
  return (
    <>
      <Grid size={3}>
        <Typography
          variant="body1"
          sx={{
            mt: '8px',
            fontWeight: 700,
            fontSize: '16px',
            color: '#000',
          }}
        >
          {name}
        </Typography>
      </Grid>
      <Grid size={6}>
        <FormControl fullWidth>
          <InputLabel id={`model-${functionModule}-label`}>
            {t('modelConfig.label')}
          </InputLabel>
          <Select
            labelId={`model-${functionModule}-label`}
            id={`model-${functionModule}`}
            label={t('modelConfig.label')}
            size="small"
            value={model}
            onChange={(e) =>
              dispatch(
                configuration.actions.updateModelConfig({
                  functionModule,
                  model: e.target.value as any,
                }),
              )
            }
          >
            {Object.entries(models).map(([modelId, modelName]) => (
              <MenuItem key={modelId} value={modelId}>
                {modelName}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {/* <Typography>{description}</Typography> */}
      </Grid>
      <Grid size="grow"></Grid>
    </>
  );
};

export const ModelConfig = () => {
  const { t } = useTranslation();
  return (
    <Box position="relative">
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        mb={2}
      >
        <Typography
          variant="body1"
          sx={{
            flexGrow: 1,
            marginRight: '40px',
            fontSize: '12px',
          }}
        >
          {t('modelConfig.description')}
        </Typography>
      </Box>
      <Grid container alignItems="flex-start" spacing={1} rowSpacing={5}>
        {Object.values(ModelConfigDtoFunctionModuleEnum)
          .filter(
            (module) =>
              module === 'textTranslation' ||
              module === 'documentTranslation',
          )
          .map((module) => (
            <ModelConfigForFunctionModule functionModule={module} key={module} />
          ))}
      </Grid>
    </Box>
  );
};
